import { ChangeEvent } from 'react';

import {
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { FormikErrors } from 'formik';

import MainCard from 'src/components/MainCard/MainCard';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

import IEBankFields, { BankField } from './IEBankFields';

type IEBankDetailsFormProps = {
    values: {
        settlementAccountSameAsChargeable: boolean;
        settlementAccountBic: string;
        settlementAccountIban: string;
        settlementAccountAccountName: string;
        settlementAccountBankName: string;
        chargeableAccountBic: string;
        chargeableAccountIban: string;
        chargeableAccountAccountName: string;
        chargeableAccountBankName: string;
    };
    errors: FormikErrors<{ [key: string]: string | undefined }>;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    setFieldValue: (field: string, value: string) => void;
};

const IEBankDetailsForm = ({
    values,
    handleChange,
    errors,
    setFieldValue,
}: IEBankDetailsFormProps) => {
    const { merchantApplication } = useAddMerchantContext();
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    const sharedBankFieldProps = [
        { label: 'BIC', placeholder: 'Enter BIC' },
        { label: 'IBAN', placeholder: 'Enter IBAN' },
        { label: 'Account Name', placeholder: 'Enter Account Name' },
        { label: 'Bank Name', placeholder: 'Enter Bank Name' },
    ];

    const settlementBankFields: BankField[] = [
        {
            name: 'settlementAccountBic',
            ...sharedBankFieldProps[0],
            chargeableField: 'chargeableAccountBic',
        },
        {
            name: 'settlementAccountIban',
            ...sharedBankFieldProps[1],
            chargeableField: 'chargeableAccountIban',
        },
        {
            name: 'settlementAccountAccountName',
            ...sharedBankFieldProps[2],
            chargeableField: 'chargeableAccountAccountName',
        },
        {
            name: 'settlementAccountBankName',
            ...sharedBankFieldProps[3],
            chargeableField: 'chargeableAccountBankName',
        },
    ];

    const chargeableBankFields: BankField[] = [
        {
            name: 'chargeableAccountBic',
            ...sharedBankFieldProps[0],
        },
        {
            name: 'chargeableAccountIban',
            ...sharedBankFieldProps[1],
        },
        {
            name: 'chargeableAccountAccountName',
            ...sharedBankFieldProps[2],
        },
        {
            name: 'chargeableAccountBankName',
            ...sharedBankFieldProps[3],
        },
    ];

    return (
        <MainCard>
            <Typography fontSize="24px" color="#575757" marginBottom="8px">
                Bank Details
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '34px',
                }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
                <FormLabel id="settlementAccountSameAsChargeable" sx={{ color: '#575757' }}>
                    Are Settlement Accounts (money paid in) the same as chargeable accounts?
                </FormLabel>
                <FormControl>
                    <RadioGroup
                        aria-labelledby="settlementAccountSameAsChargeable"
                        value={values.settlementAccountSameAsChargeable}
                        name="settlementAccountSameAsChargeable"
                        row
                        onChange={(event) => {
                            setFieldValue(
                                'settlementAccountSameAsChargeable',
                                JSON.parse(event.target.value)
                            );
                        }}
                    >
                        <FormControlLabel
                            value
                            control={<Radio />}
                            label="Yes"
                            disabled={isEditingDisabled}
                        />
                        <FormControlLabel
                            value={false}
                            control={<Radio />}
                            label="No"
                            disabled={isEditingDisabled}
                        />
                    </RadioGroup>
                </FormControl>
            </Stack>
            <Typography color="#8E8E8D" marginTop="16px">
                A separate proof of bank will be needed for the above bank details. Please make sure
                you upload this in the supporting documents step.
            </Typography>

            <IEBankFields
                heading={
                    values.settlementAccountSameAsChargeable
                        ? 'Settlement Account & Chargeable Account'
                        : 'Settlement Account'
                }
                bankFields={settlementBankFields}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                settlementAccountSameAsChargeable={values.settlementAccountSameAsChargeable}
                isEditingDisabled={isEditingDisabled}
            />

            {!values.settlementAccountSameAsChargeable && (
                <IEBankFields
                    heading="Chargeable Account"
                    bankFields={chargeableBankFields}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    isEditingDisabled={isEditingDisabled}
                />
            )}
        </MainCard>
    );
};

export default IEBankDetailsForm;
